import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Chip, Stack, SvgIconProps, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { ReadinessRequirement, ReadinessStatus } from '../../api/ach/readiness';
import useCampaignReadiness from '../../hooks/useCampaignReadiness';

// Per-requirement status → status icon + semantic color (matches the app's
// SeverityIcon idiom in Alerts/severity.tsx). UNMET is an actionable failure
// (warning); NOT_EVALUATED could not be computed (neutral/disabled grey, as the
// old 'default' chip was); MET is satisfied (success).
const STATUS_ICON: Record<
  ReadinessStatus,
  { Icon: React.ComponentType<SvgIconProps>; color: SvgIconProps['color'] }
> = {
  MET: { Icon: CheckCircleIcon, color: 'success' },
  UNMET: { Icon: CancelIcon, color: 'warning' },
  NOT_EVALUATED: { Icon: HelpOutlineIcon, color: 'disabled' },
};

// Human-readable label per status — carried by the icon's Tooltip and its
// accessible name (titleAccess) so the text meaning stays discoverable.
const STATUS_LABEL: Record<ReadinessStatus, string> = {
  MET: 'Met',
  UNMET: 'Unmet',
  NOT_EVALUATED: 'Not evaluated',
};

interface CampaignReadinessPanelProps {
  // The Rails campaign id, which in this FE is the AdGroup id.
  campaignId: number;
}

// CampaignReadinessPanel is the Tier-A, read-only publish-readiness surface for an
// AdGroup (ACH-gvkz). It renders a MET/UNMET/NOT_EVALUATED verdict from the ACH
// campaign-readiness endpoint. It NEVER blocks or breaks the AdGroup page: while
// loading, or when ACH is unreachable/errors (the hook degrades to undefined), it
// renders nothing at all.
function CampaignReadinessPanel({ campaignId }: CampaignReadinessPanelProps) {
  const { data, isLoading } = useCampaignReadiness(campaignId);

  // Quiet fallback: no skeleton, no error — the panel is simply absent until (and
  // unless) ACH returns a verdict. A page without ACH looks exactly as it did before.
  if (isLoading || !data) {
    return null;
  }

  // Default (non-verbose) view: requirements holds only the actionable rows
  // (UNMET + NOT_EVALUATED). When ready, that array is empty.
  const actionable: ReadinessRequirement[] = data.requirements;

  return (
    <Stack spacing={1} px={2} py={1} data-testid="campaign-readiness-panel">
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">Publish readiness</Typography>
        <Chip
          size="small"
          color={data.ready ? 'success' : 'warning'}
          label={data.ready ? 'Ready to publish' : 'Not ready to publish'}
        />
      </Stack>

      {!data.ready && actionable.length > 0 && (
        <Stack spacing={0.5}>
          {actionable.map((req) => {
            const { Icon, color } = STATUS_ICON[req.status];
            const label = STATUS_LABEL[req.status];
            return (
              <Stack key={req.id} direction="row" spacing={1} alignItems="flex-start">
                <Tooltip title={label} arrow placement="top">
                  <Icon
                    color={color}
                    fontSize="small"
                    aria-label={label}
                    titleAccess={label}
                    sx={{ mt: 0.25 }}
                  />
                </Tooltip>
                <Stack spacing={0}>
                  <Typography variant="body2">{req.label}</Typography>
                  {req.reason && (
                    <Typography variant="caption" color="text.secondary">
                      {req.reason}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

export default CampaignReadinessPanel;
